/**
 * Sign in and sign up.
 *
 * One file because they are the same form with two extra fields, and splitting
 * them would duplicate the error handling, the pending state and the layout.
 *
 * **The subscriber attestation is on the signup form, not buried in settings.**
 * CME requires the distributor to report subscriber category, and getting it
 * wrong takes a user from roughly $9/month per exchange to roughly $140 —
 * charged to them. Asking at signup, in plain language, with the consequence
 * stated, is the only honest place to ask it. The default is non-professional
 * because that is what the overwhelming majority of this audience is, and the
 * question says what would make it wrong.
 */
import { useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { ApiError } from '../lib/api.js';
import { Link, navigate } from '../lib/router.js';
import { useSession } from '../lib/session.js';

type Mode = 'login' | 'signup';

export function AuthScreen({ mode }: { mode: Mode }): JSX.Element {
  const { login, signup } = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [professional, setProfessional] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [retryAfterSec, setRetryAfterSec] = useState<number | null>(null);
  const [pending, setPending] = useState(false);

  const signingUp = mode === 'signup';

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (pending) return;

    setError(null);
    setRetryAfterSec(null);
    setPending(true);

    try {
      if (signingUp) {
        await signup({
          email: email.trim(),
          password,
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          subscriberCategory: professional ? 'professional' : 'non_professional',
        });
      } else {
        await login(email.trim(), password);
      }
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setRetryAfterSec(err.retryAfterSec ?? null);
      } else {
        setError('Could not reach the server. Check your connection and try again.');
      }
      setPending(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">AEGIS</div>
        <h1 className="auth-title">
          {signingUp ? 'Create your account' : 'Sign in'}
        </h1>
        <p className="auth-sub">
          {signingUp
            ? 'Charts, journal and analysis. No card required to start.'
            : 'Welcome back.'}
        </p>

        <form onSubmit={onSubmit} noValidate>
          {signingUp && (
            <div className="field-row">
              <label className="field">
                <span className="field-label">First name</span>
                <input
                  className="input"
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field-label">Last name</span>
                <input
                  className="input"
                  type="text"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </label>
            </div>
          )}

          <label className="field">
            <span className="field-label">Email</span>
            <input
              className="input"
              type="email"
              required
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field-label">Password</span>
            <input
              className="input"
              type="password"
              required
              autoComplete={signingUp ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {/* The floor is length, not composition — the server follows NIST
                SP 800-63B, so the form must not invent an uppercase-and-a-symbol
                rule the backend does not have. */}
            {signingUp && (
              <span className={`field-hint ${password && password.length < 12 ? 'field-hint-warn' : ''}`}>
                At least 12 characters. No uppercase or symbol requirement — length
                is what matters.
              </span>
            )}
          </label>

          {signingUp && (
            <fieldset className="attest">
              <legend className="field-label">Market data category</legend>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={professional}
                  onChange={(e) => setProfessional(e.target.checked)}
                />
                I am a <strong>professional</strong> subscriber
              </label>
              <p className="field-hint">
                Tick this if you trade for a firm, are registered with a regulator or
                exchange, or use market data for business purposes. Exchanges charge
                professional subscribers substantially more — roughly $140 per exchange
                per month rather than roughly $9 — and that cost is yours, not ours.
                Most individual traders using their own capital are non-professional.
                You can correct this later.
              </p>
            </fieldset>
          )}

          {error && (
            <div className="form-error" role="alert">
              {error}
              {retryAfterSec !== null && (
                <> Try again in {Math.ceil(retryAfterSec / 60)} minute
                  {Math.ceil(retryAfterSec / 60) === 1 ? '' : 's'}.</>
              )}
            </div>
          )}

          <button className="btn btn-primary btn-block" type="submit" disabled={pending}>
            {pending
              ? (signingUp ? 'Creating account…' : 'Signing in…')
              : (signingUp ? 'Create account' : 'Sign in')}
          </button>
        </form>

        <p className="auth-alt">
          {signingUp ? (
            <>Already have an account? <Link to="/login">Sign in</Link></>
          ) : (
            <>
              No account? <Link to="/signup">Create one</Link>
              {' · '}
              <Link to="/forgot-password">Forgot your password?</Link>
            </>
          )}
        </p>

        <p className="auth-legal">
          AEGIS is analytics and journaling. It does not place orders and is not an
          investment adviser. Nothing it produces is a recommendation to trade.
          {' '}<Link to="/pricing">See plans</Link>
        </p>
      </div>
    </div>
  );
}
